import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import api from "../lib/api";
import { useImages } from "../lib/images";
import Seo from "../components/Seo";

export default function About() {
  const { images: IMG } = useImages();
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/public/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="about-page">
      <Seo path="/about" title="About — our East Croydon studio & tutors"
        description="Meet Jadesola and the team behind Seasons Art Class East Croydon. A warm, welcoming studio where adults learn to draw, paint and create over 14 weeks." />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-14 grid md:grid-cols-5 gap-10 items-center">
        <div className="md:col-span-2">
          <img src={IMG.tutor} alt="East Croydon studio" className="w-full aspect-[4/5] object-cover rounded-3xl shadow-lg" />
        </div>
        <div className="md:col-span-3">
          <div className="font-hand text-[#A685BD] text-2xl">who we are</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#5D2C8F] mt-1 brush-underline">About Seasons Art Class — East Croydon</h1>
          <p className="mt-7 text-[#5A4A6B] leading-relaxed">
            We're the East Croydon branch of <strong>The Seasons Art Class®</strong> — a warm, welcoming 14-week course
            of art classes for adult beginners and improvers. We offer a high standard of tuition in a friendly, relaxed
            setting, with a cup of tea never far away.
          </p>
          <p className="mt-4 text-[#5A4A6B] leading-relaxed">
            Each term you'll draw or paint a minimum of twelve original pieces of art, across pencil drawing, oil pastels,
            watercolours and acrylics — from misty mountain tops to stunning seaside vistas, still-life compositions to
            expressive portraits. The curriculum is fresh every term, so returning students never repeat the same lesson.
          </p>
        </div>
      </section>

      {/* Seasons story */}
      <section className="bg-[#F4EDDD] py-20 border-y border-[#E8E2D5]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10">
          <div>
            <div className="font-hand text-[#8EC9C7] text-2xl">since 2009</div>
            <h2 className="font-display text-3xl text-[#5D2C8F] mt-1">The story</h2>
          </div>
          <div className="md:col-span-2 space-y-4 text-[#5A4A6B] leading-relaxed">
            <p>
              Established in 2009, Seasons Art Class® has grown to <strong>over 140 branches</strong> across the UK —
              from Scotland to the south coast — with around <strong>4,500 students</strong> developing their artistic
              skills every single week.
            </p>
            <p>
              On average, <strong>50–75% of students return</strong> to another course to keep honing their skills and
              socialise with the friends they've made. We encourage a happy, relaxed feel in our step-by-step lessons —
              tuition you'll look forward to every week.
            </p>
            <p className="italic text-[#5D2C8F]">
              "If you have the dexterity to write, then you have the ability to draw or paint. All you need is the right
              guidance to help you develop your creative skills and discover your inner artist."
            </p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20" id="team">
        <div className="text-center mb-12">
          <div className="font-hand text-[#A685BD] text-2xl">the people behind the paint</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1 brush-underline">Meet the team</h2>
        </div>
        {team.length === 0 ? (
          <div className="text-center text-[#6E6074] py-10">
            <Users className="mx-auto text-[#A685BD] mb-3" />
            Team details coming soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((t) => (
              <div key={t.id} className="soft-card p-6 text-center" data-testid={`team-card-${t.id}`}>
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-[#F4EDDD] mb-4 ring-4 ring-[#8EC9C7]/30">
                  {t.photo ? (
                    <img src={t.photo} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-4xl text-[#5D2C8F]">
                      {t.name?.[0] || '?'}
                    </div>
                  )}
                </div>
                <div className="font-display text-2xl text-[#5D2C8F]">{t.name}</div>
                <div className="font-hand text-[#A685BD] text-lg mt-1">{t.role}</div>
                {t.bio && <p className="text-sm text-[#5A4A6B] mt-4 leading-relaxed">{t.bio}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Gen quote */}
      <section className="bg-[#F4EDDD] py-20 border-y border-[#E8E2D5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-hand text-[#8EC9C7] text-2xl">from Gen, our founder</div>
          <p className="font-display italic text-2xl md:text-3xl text-[#5D2C8F] mt-4 leading-snug">
            "This is more than 'just' an art class. It's your new hobby, it's your new set of friends
            and local support network — a skill you get to take away and enjoy long after class is over."
          </p>
        </div>
      </section>
    </div>
  );
}
